'use client';

import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {X, User, UserCheck, DollarSign} from 'lucide-react';
import {
    useCreateAppointmentScreen
} from "../../components/crudAppointmentScreens/createAppointmentScreen/useCreateAppointmentScreen";
import {addMinutes, differenceInMinutes, format, isValid} from "date-fns";
import AsyncSelect from "react-select/async";
import {CustomClientOption} from "../../components/crudAppointmentScreens/components/customClientOption";
import {AsInput, reactSelectStyles} from "../../../subframe";
import {CustomEmployeeOption} from "../../components/crudAppointmentScreens/components/customEmployeeOption";
import {CustomServiceOption} from "../../components/crudAppointmentScreens/components/customServiceOption";
import Select, {components} from "react-select";
import {formatAsUTC} from "../../../utils/formatDate";
import {adjustDateToUTC} from "../../../utils/formatTime";
import AvailableSlotSelect, {EventTypeD} from "../ui/AvailableSlotSelect";
import {AppointmentEvent, MyCalendarEvent} from "../hooks/useCalendarState";
import {Spinner} from "@nextui-org/react";
import CreatableSelect from 'react-select/creatable';
import {toast} from "react-toastify";
import {debounce} from "lodash";
import {fetchClientPage} from "../../../redux/clientsSlice";

const toClientOption = (c: any) => ({
    value: c.id,
    label: c.name,
    phone: c.phone,
    email: c.email,
    createdAt: c.createdAt,
    avatar: c.avatar,
});

const CLIENT_PAGE_SIZE = 25;

// Lists the client options and loads the next page once the bottom of the list scrolls into view.
// An IntersectionObserver is used instead of react-select's onMenuScrollToBottom, which doesn't fire
// on touch devices (phones/tablets) and only fires once per arrival at the bottom.
const ClientMenuList = (props: any) => {
    const {onLoadMoreClients, hasMoreClients, isLoadingMoreClients} = props.selectProps;
    const sentinelRef = useRef<HTMLDivElement>(null);
    const optionCount = React.Children.count(props.children);

    useEffect(() => {
        const sentinel = sentinelRef.current;
        if (!sentinel || !hasMoreClients) return;
        const observer = new IntersectionObserver((entries) => {
            if (entries[0]?.isIntersecting) onLoadMoreClients?.();
        });
        observer.observe(sentinel);
        return () => observer.disconnect();
    }, [hasMoreClients, onLoadMoreClients, optionCount]);

    return (
        <components.MenuList {...props}>
            {props.children}
            {isLoadingMoreClients && (
                <div className="px-3 py-2 text-xs text-center text-gray-500">Loading more clients...</div>
            )}
            {hasMoreClients && <div ref={sentinelRef} className="h-px"/>}
        </components.MenuList>
    );
};

export const getServiceTotals = (services: any) => {
    if (!Array.isArray(services) || services.length === 0) {
        return {totalCents: 0, formattedPrice: "$0.00", totalDuration: 0};
    }

    const totals = services.reduce(
        (acc, service) => {
            if (!service) return acc;
            const priceCents =
                service.priceCents ??
                service.businessService?.priceCents ??
                0;

            const duration =
                service.duration ??
                service.businessService?.duration ??
                0;

            acc.totalCents += priceCents;
            acc.totalDuration += duration;

            return acc;
        },
        {totalCents: 0, totalDuration: 0}
    );

    const formattedPrice = `$${(totals.totalCents / 100).toFixed(2)}`;
    const price = (totals.totalCents / 100).toFixed(2)

    return {...totals, formattedPrice, price};
};

interface BookingModalProps {
    onClose: () => void;
    isOpen: boolean;
    // createNewPendingAppointment:any;
    pendingAppointment: any;
    setPendingAppointment: (pendingAppointment: any) => void;
    toggleAppointmentDrawer: (open: boolean) => void;
    getSchedulesOfEmployees: ({currentDate}: { currentDate?: Date }) => void;
    getWeeklySchedulesOfEmployees: ({
                                        employeeId,
                                        date,
                                    }: {
        employeeId: number;
        date: Date;
    }) => void;
    selectedEmployee: any;
    setSelectedEmployee: any;
    calendarView: "day" | "week" | "month";
    events: MyCalendarEvent[];
    updateCalendarData?: any;


}

export const BookingModal: React.FC<BookingModalProps> = ({

                                                              pendingAppointment,
                                                              setPendingAppointment,
                                                              toggleAppointmentDrawer,
                                                              getSchedulesOfEmployees,
                                                              getWeeklySchedulesOfEmployees,
                                                              selectedEmployee,
                                                              setSelectedEmployee,
                                                              calendarView,

                                                              onClose,
                                                              isOpen,
                                                              events,
                                                              updateCalendarData

                                                          }) => {

    const [editData, setEditData] = useState(pendingAppointment);
    const [slot, setSlot] = useState("");
    // Add these inside your BookingModal component
    const [isCreatingClient, setIsCreatingClient] = useState(false);
    const [newClientData, setNewClientData] = useState({name: '', phone: '', email: ''});


    const {
        employee,
        employees,
        clients,
        onSubmit,
        onHandleClientChange,
        selectedClient,
        setSelectedClient,
        selectedServices,
        onHandleAddService,
        availableServices,
        serviceSelectValue,
        onRemoveService,
        handleClientSearch,
        selectedDate,
        onDateChange,
        onTimeChange,
        isDateInputFocused,
        setIsDateInputFocused,
        setSelectedDate,
        createAppointmentLoading,
        notes,
        setNotes
    } = useCreateAppointmentScreen({
        pendingAppointment,
        setPendingAppointment,
        toggleAppointmentDrawer,
        onCloseBookingModal: onClose,
        getSchedulesOfEmployees,
        getWeeklySchedulesOfEmployees,
        selectedEmployee,
        calendarView,
        updateCalendarData,

    });


    const onTimeChangeB = useCallback(
        (startTime: string) => {
            if (!isValid(pendingAppointment?.start) || !isValid(pendingAppointment?.end)) return;

            const formattedDate = format(new Date(selectedDate as any), "yyyy-MM-dd");

            const formattedStartDateTime = `${formattedDate}T${startTime}`;
            const formattedDateEndTime = `${formattedDate}T${format(
                addMinutes(
                    formattedStartDateTime,
                    differenceInMinutes(pendingAppointment?.end, pendingAppointment?.start)
                ),
                "HH:mm"
            )}`;

            const dateTimeStartObject = new Date(formattedStartDateTime);
            const dateTimeEndObject = new Date(formattedDateEndTime);

            setPendingAppointment({
                ...pendingAppointment,
                start: dateTimeStartObject,
                end: dateTimeEndObject,
            });
        },
        [pendingAppointment]
    );


    const getEndTimeFromDuration = (
        startTime: Date | string,
        totalDuration: number
    ): string => {
        if (!startTime || !isValid(new Date(startTime)) || !totalDuration) {
            return "";
        }

        const startDate = new Date(startTime);
        const endDate = addMinutes(startDate, totalDuration);

        return format(endDate, "hh:mm a"); // Example: "11:20 AM"
    };


    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        // Email is optional for a new client (family members often share one phone); phone is required
        if (newClientData.name && !newClientData?.phone) {
            toast.error("Please enter phone");
            return;
        }



        // If creating a new client, merge that data into what we send to onSubmit
        const submissionData = isCreatingClient
            ? {...pendingAppointment, client: newClientData}
            : pendingAppointment;




        // You might need to adjust how your hook handles this,
        // but this ensures the data exists in the event.
        // await onSubmit(e, isCreatingClient ? newClientData : null);


        await onSubmit(e)

        // createNewPendingAppointment({
        //     ...formData,
        //     day: selectedSlot.day,
        //     timeSlot: selectedSlot.timeSlot,
        // });
        // onClose();
    };


    // Client picker: pages through the server (empty search = every client) and loads the next
    // page when the list is scrolled to the bottom, so no client is ever cut off.
    const [clientOptions, setClientOptions] = useState<any[]>([]);
    const [clientSearch, setClientSearch] = useState('');
    const [clientPage, setClientPage] = useState(0);
    const [clientHasMore, setClientHasMore] = useState(true);
    const [isLoadingClients, setIsLoadingClients] = useState(false);
    // Only the latest request may update the list, so a slow old page can't overwrite a new search
    const clientRequestId = useRef(0);

    const loadClientPage = useCallback(async (term: string, page: number) => {
        const requestId = ++clientRequestId.current;
        setIsLoadingClients(true);
        try {
            const {clients: results, totalCount} = await fetchClientPage(term, page, CLIENT_PAGE_SIZE);
            if (requestId !== clientRequestId.current) return;
            const newOptions = results.map(toClientOption);
            setClientOptions((prev) => {
                const merged = page === 1
                    ? newOptions
                    : [...prev, ...newOptions.filter((o: any) => !prev.some((p) => p.value === o.value))];
                setClientHasMore(results.length > 0 && merged.length < totalCount);
                return merged;
            });
            setClientPage(page);
        } catch (error) {
            if (requestId !== clientRequestId.current) return;
            console.error("Error loading clients:", error);
            setClientHasMore(false);
        } finally {
            if (requestId === clientRequestId.current) setIsLoadingClients(false);
        }
    }, []);

    const searchClients = useMemo(
        () => debounce((term: string) => loadClientPage(term, 1), 300),
        [loadClientPage]
    );

    useEffect(() => () => searchClients.cancel(), [searchClients]);

    const onClientInputChange = (inputValue: string, {action}: { action: string }) => {
        if (action === 'input-change') {
            setClientSearch(inputValue);
            // Don't page the old results while the new search is pending
            setClientHasMore(false);
            searchClients(inputValue.trim());
        } else if ((action === 'menu-close' || action === 'input-blur' || action === 'set-value') && clientSearch) {
            // react-select clears the typed text here — go back to the full list
            setClientSearch('');
            searchClients.cancel();
            loadClientPage('', 1);
        }
    };

    // Refresh on every open so clients added since the last open show up too
    const onClientMenuOpen = () => {
        if (!clientSearch) loadClientPage('', 1);
    };

    const loadMoreClients = useCallback(() => {
        if (clientHasMore && !isLoadingClients) loadClientPage(clientSearch.trim(), clientPage + 1);
    }, [clientHasMore, isLoadingClients, loadClientPage, clientSearch, clientPage]);

    // Handle selection or creation

    const onClientSelectChange = (newValue: any) => {
        // 1. Handle "Clear" (X button clicked)
        if (!newValue) {
            setIsCreatingClient(false);
            setSelectedClient(null);
            setNewClientData({name: '', phone: '', email: ''});
            return;
        }

        // 2. Handle "Create New Client"
        const {value,...rest}=newValue
        if (newValue.__isNew__) {
            setIsCreatingClient(true);
            setSelectedClient(null);
            setNewClientData({...newClientData, name: newValue.label});
            onHandleClientChange(rest);
        }
        // 3. Handle "Select Existing Client"
        else {
            setIsCreatingClient(false);
            setNewClientData({name: '', phone: '', email: ''});
            // Keep value so the hook gets the client's id
            onHandleClientChange(newValue);
        }
    };


    if (!isOpen) return null; // ✅ Don’t render if closed
    return (
        <div
            className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 transition-opacity"
            onClick={onClose}
        >
            {/* Modal content */}
            <div
                className="bg-white rounded-lg shadow-2xl w-full max-w-2xl mx-auto p-6 max-h-[90vh] overflow-y-auto transform transition-transform duration-200 scale-100"
                onClick={(e) => e.stopPropagation()} // prevent closing on content click
            >
                {/* Header */}
                <div className="flex justify-between items-center mb-4 border-b pb-3">
                    <h2 className="text-xl font-semibold">New Appointment</h2>
                    <button
                        onClick={onClose}
                        className="p-1 hover:bg-gray-100 rounded-full transition"
                    >
                        <X className="w-5 h-5 text-gray-700"/>
                    </button>
                </div>

                <div className="flex items-center gap-2.5 mb-6">
                    <AsInput
                        aria-label="Date Input"
                        key={isDateInputFocused ? "is-focused-date" : "is-not-focused-date"}
                        name="date"
                        textPosition="left"
                        leading="On"
                        defaultValue={
                            isDateInputFocused
                                ? format(new Date(selectedDate), "yyyy-MM-dd")
                                : format(new Date(selectedDate), "EEE, MMM d")
                        }
                        type={isDateInputFocused ? "date" : "string"}
                        containerClassName="w-full"
                        onFocus={() => setIsDateInputFocused(true)}
                        onChange={(e) => onDateChange(new Date(e.target.value))}
                        onBlur={(e: any) => {
                            const dateValue = e.target.value;
                            if (dateValue) {
                                const adjustedDate = adjustDateToUTC(dateValue);
                                setSelectedDate(adjustedDate.toISOString());
                            }
                            setIsDateInputFocused(false);
                        }}
                    />
                </div>


                {/* Form */}
                <form onSubmit={handleSubmit} className="space-y-4">

                    <div className={'flex flex-col w-full gap-4'}>
                        {/* Practitioner */}
                        <div className={'w-full'}>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                <UserCheck className="w-4 h-4 inline mr-1"/> Practitioner *
                            </label>
                            <select
                                required
                                value={pendingAppointment?.employee?.id}
                                onChange={(e) => {
                                    const emp = employees.find((em) => em.id.toString() === e.target.value)
                                    // setSelectedEmployee(emp)
                                    setPendingAppointment((prev: any) => ({...prev, employee: emp}))
                                    // setFormData((prev:async ) => ({ ...prev, practitioner: e.target.value }))
                                }}
                                className="w-full border px-3 py-2 rounded focus:ring-2 focus:ring-blue-500"
                            >
                                <option value="">Select practitioner</option>
                                {employees.map((em) => (
                                    <option key={em.id} value={em.id}>
                                        {em.name}
                                    </option>
                                ))}
                            </select>

                        </div>


                        {/* Patient Name Section */}
                        <div className={'w-full'}>

                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                <User className="w-4 h-4 inline mr-1"/> Client Name *
                            </label>

                            {selectedClient && !isCreatingClient ? (
                                // --- VIEW EXISTING CLIENT MODE ---
                                <div className="border w-full p-4 rounded-lg flex justify-between items-start">
                                    <div>
                                        <p className="font-semibold">{selectedClient.name}</p>
                                        <p className="text-sm text-gray-500">
                                            Client since {format(new Date(selectedClient.createdAt), "M.d.yyyy")}
                                        </p>
                                        {selectedClient?.phone && <p className="text-sm">📞 {selectedClient.phone}</p>}
                                        {selectedClient?.email && <p className="text-sm">✉️ {selectedClient.email}</p>}
                                    </div>
                                    <button
                                        type="button"
                                        className="text-blue-600 text-sm"
                                        onClick={() => setSelectedClient(null)}
                                    >
                                        Change
                                    </button>
                                </div>
                            ) : (
                                // --- SEARCH OR CREATE MODE ---
                                <CreatableSelect
                                    isClearable
                                    onChange={onClientSelectChange}

                                    placeholder="Search name, phone or email"

                                    // Results come from the server page by page; scrolling to the bottom loads more
                                    components={{MenuList: ClientMenuList}}
                                    {...({
                                        isLoadingMoreClients: isLoadingClients && clientPage > 0 && clientOptions.length > 0,
                                        hasMoreClients: clientHasMore && !isLoadingClients,
                                        onLoadMoreClients: loadMoreClients,
                                    } as any)}
                                    options={clientOptions}
                                    onInputChange={onClientInputChange}
                                    onMenuOpen={onClientMenuOpen}
                                    isLoading={isLoadingClients && clientOptions.length === 0}
                                    noOptionsMessage={() => (isLoadingClients ? 'Loading clients...' : 'No clients found')}
                                    filterOption={null}
                                    // 2. Custom Look for Options
                                    formatOptionLabel={(option: any) => (
                                        <div className="flex flex-col gap-0.5">
                                            {/* Main Label (Name) */}
                                            <span className="font-medium text-gray-900">
                {option.label}
            </span>

                                            {/* Subtitle (Phone/Email) - Only show if it's NOT the "Create..." option */}
                                            {!option.__isNew__ && (
                                                <div className="flex items-center gap-2 text-xs text-gray-500">
                                                    {option.phone && (
                                                        <span className="flex items-center gap-1">
                            📞 {option.phone}
                        </span>
                                                    )}
                                                    {option.email && (
                                                        <span className="flex items-center gap-1">
                             ✉️ {option.email}
                        </span>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    )}


                                    // Configuration to allow creating new items
                                    formatCreateLabel={(inputValue) => `Add new client: "${inputValue}"`}
                                    styles={reactSelectStyles({borderContainer: true})}
                                    // Ensure the value reflects the new name if we are in creation mode
                                    value={isCreatingClient ? {label: newClientData.name, value: 'new'} : null}
                                />
                            )}
                        </div>

                        {/* Contact Info - Only Show for New Clients */}
                        {isCreatingClient && (
                            <div
                                className="flex flex-col gap-2 w-full mt-2 p-3 bg-gray-50 rounded border border-blue-100 animate-in fade-in slide-in-from-top-2">
                                <p className="text-xs font-semibold text-blue-600 uppercase tracking-wide">
                                    New Client Details
                                </p>
                                <div className="flex flex-1 gap-2 w-full">
                                    <div className="w-full">
                                        <label className="text-xs text-gray-500">Phone *</label>
                                        <input
                                            required
                                            type="tel"
                                            placeholder="Phone Number"
                                            value={newClientData.phone}
                                            onChange={(e) => {
                                                setNewClientData((prev) => ({...prev, phone: e.target.value}))
                                                onHandleClientChange({...selectedClient,phone:e.target.value});
                                                setPendingAppointment((prev: any) => ({...prev, client: {...prev.client,phone:e.target.value}}))
                                            } }
                                            className="w-full border px-3 py-2 rounded focus:ring-2 focus:ring-blue-500 bg-white"
                                        />
                                    </div>
                                    <div className="w-full">
                                        <label className="text-xs text-gray-500">Email</label>
                                        <input
                                            type="email"
                                            placeholder="Email Address"
                                            value={newClientData.email}
                                            onChange={(e) => {
                                                setNewClientData((prev) => ({...prev, email: e.target.value}))
                                                onHandleClientChange({...selectedClient,email:e.target.value});
                                                setPendingAppointment((prev: any) => ({...prev, client: {...prev.client,email:e.target.value}}))

                                            }}

                                            className="w-full border px-3 py-2 rounded focus:ring-2 focus:ring-blue-500 bg-white"
                                        />
                                    </div>
                                </div>
                            </div>
                        )}

                    </div>
                    <Select
                        placeholder="Search or select a service"
                        components={{
                            Option: CustomServiceOption,
                        }}
                        onChange={onHandleAddService}
                        value={serviceSelectValue}
                        className="text-zinc-500 text-sm mobile:text-medium"
                        options={availableServices?.map((category) => ({
                            label: category.name,
                            options: category.services.map((service: any) => ({
                                value: service.id,
                                label: service.title,
                                price: service.price,
                                status: service.status,
                                employee,
                            })),
                        }))}
                        styles={reactSelectStyles({
                            borderContainer: true,
                        })}
                    />
                    {
                        selectedServices &&
                        <SelectedServicesList
                            selectedServices={selectedServices}
                            onServiceRemove={onRemoveService}
                        />

                    }
                    {/* Time & Duration */}
                    <div className="grid grid-cols-3 gap-2">


                        <div>
                            <AvailableSlotSelect
                                events={events}
                                resourceId={pendingAppointment?.employee?.id}
                                date={selectedDate}
                                slotInterval={10}
                                // selectedSlot={slot}
                                selectedSlot={format(pendingAppointment?.start, "HH:mm")}
                                onSelect={(slot) => {
                                    setSlot(slot)
                                    onTimeChangeB(slot)
                                }}
                                duration={getServiceTotals(pendingAppointment?.services)?.totalDuration}
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                End Time
                            </label>
                            <input
                                type="text"
                                value={getEndTimeFromDuration(pendingAppointment?.start, getServiceTotals(pendingAppointment?.services)?.totalDuration)}
                                readOnly
                                className="w-full border px-3 py-2 rounded bg-gray-50"
                            />
                        </div>


                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Duration (min)
                            </label>
                            <input
                                disabled
                                type="number"
                                min={15}
                                step={15}
                                // defaultValue={pendingAppointment?.duration}
                                value={getServiceTotals(pendingAppointment?.services)?.totalDuration}
                                // onChange={(e) =>
                                // handleDurationChange(parseInt(e.target.value))

                                //      }
                                className="w-full border px-3 py-2 rounded focus:ring-2 focus:ring-blue-500"
                            />
                        </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">

                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                <DollarSign className="w-4 h-4 inline mr-2"/>
                                Price
                            </label>
                            <input
                                disabled
                                type="number"
                                value={getServiceTotals(pendingAppointment?.services)?.price}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                min="0"
                            />

                        </div>
                    </div>

                    {/* Appointment Notes */}
                    <div className="flex flex-col gap-1 w-full">
                        <label className="block text-sm font-medium text-gray-700">Notes</label>
                        <textarea
                            value={notes}
                            onChange={(e) => setNotes(e.target.value)}
                            rows={3}
                            placeholder="Add a note..."
                            className="w-full text-sm border px-3 py-2 rounded focus:ring-2 focus:ring-blue-500 resize-none focus:outline-none bg-white text-zinc-900"
                        />
                    </div>

                    {/* Submit */}
                    <div className="flex justify-end pt-4 border-t border-gray-200">
                        <button
                            type="button"
                            onClick={onClose}
                            className="px-4 py-2 border rounded mr-2"
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            disabled={createAppointmentLoading}
                            className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
                        >
                            Create Appointment {createAppointmentLoading && <Spinner size={"sm"}/>}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};


interface Service {
    id: string | number;
    title: string;
    duration: number;
    price: number;
}

interface SelectedServicesListProps {
    selectedServices: Service[];
    onServiceRemove: (index: number) => void;
}

const SelectedServicesList: React.FC<SelectedServicesListProps> = ({
                                                                       selectedServices,
                                                                       onServiceRemove,
                                                                   }) => {
    if (!selectedServices || selectedServices.length === 0) {
        return (
            <div className="p-4 text-gray-500 text-sm text-center border border-dashed border-gray-300 rounded-lg">
                No services selected
            </div>
        );
    }

    return (
        <div className="space-y-2">
            {/* An entry can be undefined when the picked service isn't provided by the
                selected staff — key={service.id} then crashed the whole app. Keep the
                original index so onServiceRemove(index) still targets the right entry. */}
            {selectedServices.map((service, index) => service && (
                <div
                    key={service.id ?? index}
                    className="flex items-center justify-between bg-gray-50 border border-gray-200 rounded-lg p-3 hover:bg-gray-100 transition"
                >
                    <div className="flex flex-col">
                        <span className="font-medium text-gray-900">{service?.title}</span>
                        <span className="text-sm text-gray-500">
              {service?.duration} min • {service?.price}
            </span>
                    </div>

                    <button
                        type={'button'}
                        onClick={() => onServiceRemove(index)}
                        className="text-gray-400 hover:text-red-500 transition"
                        aria-label="Remove service"
                    >
                        <X className="w-4 h-4"/>
                    </button>
                </div>
            ))}
        </div>
    );
};

export default SelectedServicesList;
